/**
 * Authentication Service - Handles auth-related API calls
 */

import { userServiceApi, gamificationServiceApi, SERVICE_URLS } from './api';
import TokenManager from '@/features/authentication/services/tokenManager';

// Types for API responses
export interface SendCodeResponse {
  status: 'login' | 'registration';
  message: string;
  phone_number: string;
}

export interface VerifyCodeResponse {
  status: string;
  message: string;
  phone_number: string;
  action: 'login' | 'register';
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
}

export interface CreateGuestResponse {
  status: string;
  message: string;
  phone_number: string | null;
  action: 'guest_created';
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface RefreshTokenResponse {
  message: string;
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

export interface TokenData {
  access_token: string;
  expires_at: number;
}

export interface StoreTokenOptions {
  persistNative?: boolean;
}

// Authenticated user profile (GET /api/auth/me)
export interface UserProfile {
  username: string | null;
  phone_number: string | null;
  email: string | null;
  name: string | null;
  daily_goal: number | null;
  streak_goal: number | null;
  xp: number;
  level: number;
  current_level_xp: number;
  next_level_xp: number;
  gems: number;
  age: number | null;
  avatar_url: string | null;
  referral_code: string | null;
  active_boost_type: 'xp_boost_1_5x' | 'xp_boost_2x' | null;
  active_boost_remaining_hours: number | null;
  is_guest: boolean;
  is_registered: boolean;
  // Legacy support
  total_xp?: number;
  time_to_spend?: number | null;
}

// Registration types
export interface RegisterRequest {
  phone_number: string;
  name: string;
  source?: string;
  topic_ids?: number[];
  referral_code?: string;
  grant_default_trial?: boolean;
}

export interface RegisterResponse {
  message: string;
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

export interface ReferralValidationResponse {
  is_valid: boolean;
  message: string;
}

export interface ReferralStage {
  stage_number: number;
  gems: number;
  achieved: boolean;
}

export interface ReferralInvitee {
  name: string;
  avatar_url: string | null;
}

export interface ReferralLeagueEntry {
  rank: number;
  name: string;
  avatar_url: string | null;
  gems: number;
}

export interface ReferralLeagueCurrentUser {
  rank: number;
  name: string;
  avatar_url: string | null;
  gems: number;
}

export interface ReferralsMeResponse {
  my_referral_code: string;
  total_referrals: number;
  total_reward_gems: number;
  reward_stages: ReferralStage[];
  referrals: ReferralInvitee[];
  referral_league: ReferralLeagueEntry[];
  current_user_league: ReferralLeagueCurrentUser | null;
}

// Badge/Achievement types
export interface Badge {
  badge_id: number;
  badge_name: string;
  description: string;
  icon: string | null;
  state: 'locked' | 'unlock' | 'unlocked' | 'lock';
}

export interface BadgesData {
  total_badges: number;
  user_badge_count: number;
  badges: Badge[];
}

export interface BadgesResponse {
  success: boolean;
  message: string;
  data: BadgesData;
}

/**
 * Send verification code to phone number
 * @param phoneNumber - User's phone number
 * @returns Promise with response indicating login or registration flow
 */
export const sendVerificationCode = async (phoneNumber: string): Promise<SendCodeResponse> => {
  console.log('🔐 [AuthService] Sending verification code to:', phoneNumber);

  try {
    const response = await userServiceApi.post<SendCodeResponse>('/auth/send-code', {
      phone_number: phoneNumber.trim()
    });

    if (response.success && response.data) {
      console.log('✅ [AuthService] Verification code sent successfully:', {
        status: response.data.status,
        phone_number: response.data.phone_number,
        message: response.data.message
      });

      return response.data;
    } else {
      console.error('❌ [AuthService] Failed to send verification code:', response.error);
      throw new Error(response.error || 'Failed to send verification code');
    }
  } catch (error) {
    console.error('🚨 [AuthService] Error sending verification code:', error);
    throw error instanceof Error ? error : new Error('Unknown error occurred');
  }
};

export const validateReferralCode = async (
  referralCode: string
): Promise<ReferralValidationResponse> => {
  console.log('🎟️ [AuthService] Validating referral code');

  try {
    const response = await userServiceApi.post<ReferralValidationResponse>('/auth/referrals/validate', {
      referral_code: referralCode.trim()
    });

    if (response.data) {
      return response.data;
    }

    throw new Error(response.detail || response.error || 'خطا در اعتبارسنجی کد معرفی');
  } catch (error) {
    console.error('🚨 [AuthService] Error validating referral code:', error);
    throw error instanceof Error ? error : new Error('خطا در اعتبارسنجی کد معرفی');
  }
};

export const getMyReferrals = async (): Promise<ReferralsMeResponse> => {
  console.log('🎁 [AuthService] Fetching referral summary (/auth/referrals/me)');

  const response = await userServiceApi.get<ReferralsMeResponse>('/auth/referrals/me');
  if (response.success && response.data) {
    return response.data;
  }

  throw new Error(response.error || 'Failed to fetch referral summary');
};


/**
 * Verify OTP code for login/registration
 * @param phoneNumber - User's phone number
 * @param code - Verification code
 * @returns Promise with verification result
 */
export const verifyCode = async (
  phoneNumber: string,
  code: string
): Promise<VerifyCodeResponse> => {
  console.log('🔐 [AuthService] Verifying code for:', { phoneNumber });

  try {
    const response = await userServiceApi.post<VerifyCodeResponse>('/auth/verify-code', {
      phone_number: phoneNumber.trim(),
      code: code.trim()
    });

    if (response.success && response.data) {
      console.log('✅ [AuthService] Code verified successfully:', {
        status: response.data.status,
        action: response.data.action,
        hasAccessToken: !!response.data.access_token,
        hasRefreshToken: !!response.data.refresh_token,
        message: response.data.message
      });

      return response.data;
    } else {
      console.error('❌ [AuthService] Failed to verify code:', response.error);
      const errorMessage = response.detail || response.error || 'Invalid verification code';
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('🚨 [AuthService] Error verifying code:', error);
    throw error instanceof Error ? error : new Error('Unknown error occurred');
  }
};

/**
 * Create guest user and receive access token for authenticated onboarding calls
 */
export const createGuestUser = async (): Promise<CreateGuestResponse> => {
  const response = await userServiceApi.post<CreateGuestResponse>('/auth/create-guest', {});
  if (response.success && response.data) {
    console.log('✅ [AuthService] Guest user created:', {
      hasAccessToken: !!response.data.access_token,
      hasRefreshToken: !!response.data.refresh_token,
      expiresIn: response.data.expires_in
    });
    return response.data;
  }
  throw new Error(response.error || 'Failed to create guest user');
};


/**
 * Store JWT tokens in secure storage
 * @param accessToken - Access token
 * @param refreshToken - Refresh token
 * @param expiresIn - Token expiration time in seconds
 */
export const storeTokens = async (
  accessToken: string,
  refreshToken: string,
  expiresIn: number = 300,  // 5 minutes for testing (was 1800)
  options: StoreTokenOptions = {}
): Promise<void> => {
  const tm = TokenManager.getInstance();
  await tm.setTokens(accessToken, refreshToken, expiresIn, options);
  console.log('💾 [AuthService] Tokens stored', { expiresIn });
};


/**
 * Get valid access token, refreshing if necessary
 * @returns Promise with valid access token or null if failed
 */
export const getValidAccessToken = async (): Promise<string | null> => {
  const tm = TokenManager.getInstance();
  return await tm.getAccessToken();
};

export const hasStoredSession = async (): Promise<boolean> => {
  const tm = TokenManager.getInstance();
  return await tm.hasStoredSession();
};

export const hasPersistentNativeSession = (): boolean => {
  const tm = TokenManager.getInstance();
  return tm.hasPersistentNativeSession();
};

export const getLastRefreshFailureKind = (): 'auth' | 'network' | 'server' | 'no_session' | null => {
  const tm = TokenManager.getInstance();
  return tm.getLastRefreshFailureKind();
};

/**
 * Clear stored JWT tokens from storage
 */
export const clearStoredTokens = async (): Promise<void> => {
  const tm = TokenManager.getInstance();
  await tm.clearTokens();
  console.log('🗑️ [AuthService] Tokens cleared');
};

/**
 * Logout user by calling backend and clearing tokens
 * @returns Promise with logout message
 */
export const logoutUser = async (): Promise<string> => {
  console.log('🚪 [AuthService] Logging out user...');
  // Suppress auth redirects for a short window to avoid Welcome->Login auto jumps
  if (typeof window !== 'undefined') {
    (window as any).__suppressAuthRedirect = true;
    setTimeout(() => { (window as any).__suppressAuthRedirect = false; }, 3000);
  }

  try {
    const response = await userServiceApi.post<{ message: string }>('/auth/logout', {});

    if (response.success && response.data) {
      console.log('✅ [AuthService] Logout successful:', response.data.message);
      await clearStoredTokens();
      return response.data.message;
    } else {
      console.error('❌ [AuthService] Logout failed:', response.error);
      // Clear tokens anyway even if backend call fails
      await clearStoredTokens();
      throw new Error(response.error || 'Logout failed');
    }
  } catch (error) {
    console.error('🚨 [AuthService] Error during logout:', error);
    // Clear tokens anyway even if backend call fails
    await clearStoredTokens();
    throw error instanceof Error ? error : new Error('Unknown logout error');
  }
};

/**
 * Convenience logout helper: clear tokens only (no backend call)
 */
export const logout = async (): Promise<void> => {
  // Suppress auth redirects for a short window to avoid Welcome->Login auto jumps
  if (typeof window !== 'undefined') {
    (window as any).__suppressAuthRedirect = true;
    setTimeout(() => { (window as any).__suppressAuthRedirect = false; }, 3000);
  }
  await clearStoredTokens();
};

/**
 * Check if user is authenticated (has valid tokens)
 * @returns Promise with boolean indicating authentication status
 */
export const isAuthenticated = async (): Promise<boolean> => {
  const token = await getValidAccessToken();
  return token !== null;
};

/**
 * Get current authenticated user (validates token)
 * - On 401, api.ts will auto-refresh and retry once
 */
export const getMe = async (): Promise<UserProfile> => {
  console.log('👤 [AuthService] Fetching current user profile (/auth/me)');
  const res = await userServiceApi.get<UserProfile>('/auth/me');
  if (res.success && res.data) {
    return res.data;
  }
  throw new Error(res.error || 'Failed to fetch user profile');
};

/**
 * Update current authenticated user fields (PUT /api/auth/me)
 * Accepts any subset of: name, username, phone_number, email, avatar_url
 * Returns the updated UserProfile on success
 */
export const updateMe = async (
  payload: Partial<Pick<UserProfile, 'name' | 'username' | 'phone_number' | 'email' | 'avatar_url'>>
): Promise<UserProfile> => {
  // Remove undefined and empty-string fields to avoid sending empty updates
  const cleaned: Record<string, unknown> = {};
  (['name', 'username', 'phone_number', 'email', 'avatar_url'] as const).forEach((k) => {
    const v = (payload as any)[k];
    if (v !== undefined && v !== '') cleaned[k] = v;
  });

  const keys = Object.keys(cleaned);
  console.log('✏️  [AuthService] Updating profile via PUT /auth/me with keys:', keys, 'payload:', cleaned);
  if (keys.length === 0) {
    throw new Error('هیچ داده‌ای برای به‌روزرسانی ارسال نشد');
  }

  // Build FormData because backend expects form data
  const form = new FormData();
  keys.forEach((k) => {
    const v = cleaned[k];
    if (v !== undefined && v !== null) form.append(k, String(v));
  });
  console.log('📦 [AuthService] FormData to send:', keys.reduce((acc: any, k) => { acc[k] = String(cleaned[k] as any); return acc; }, {}));

  const res = await userServiceApi.put<UserProfile>('/auth/me', form);
  if (res.success && res.data) {
    console.log('✅ [AuthService] Profile updated');
    return res.data;
  }
  const message = res.error || 'Failed to update profile';
  console.error('❌ [AuthService] Update failed:', message);
  throw new Error(message);
};

/**
 * Get user badges/achievements
 * @returns Promise with badges data
 */
export const getUserBadges = async (): Promise<BadgesResponse> => {
  console.log('🏆 [AuthService] Fetching user badges (GAMIFICATION /user-badges/my-badges)');
  const res = await gamificationServiceApi.get<any>('/user-badges/my-badges');
  if (res.success && res.data) {
    // ApiClient wraps the backend JSON into { success: true, data: <backend_body>, message }
    // Backend body shape: { success, message, data: { total_badges, user_badge_count, badges } }
    const backend = res.data as {
      success: boolean;
      message?: string;
      data: BadgesData;
    };

    const payload: BadgesResponse = {
      success: backend.success,
      message: backend.message || 'User badges retrieved successfully',
      data: backend.data
    };
    console.log('✅ [AuthService] Badges fetched successfully:', {
      total: payload.data.total_badges,
      unlocked: payload.data.user_badge_count,
      badgesCount: payload.data.badges?.length ?? 0
    });
    return payload;
  }
  throw new Error(res.error || 'Failed to fetch user badges');
};

/**
 * Register user with collected onboarding data
 * @param payload - Simplified registration payload
 */
export const registerUser = async (
  payload: RegisterRequest
): Promise<RegisterResponse> => {
  console.log('📝 [AuthService] Registering user with payload:', {
    phone_number: payload.phone_number,
    name: payload.name,
    source: payload.source,
    topicCount: payload.topic_ids?.length || 0
  });

  // Log the full payload for debugging
  console.log('🔍 [AuthService] Full registration payload:', payload);

  // Validate phone_number is present
  if (!payload.phone_number) {
    throw new Error('Phone number is required');
  }

  try {
    const response = await userServiceApi.post<RegisterResponse>('/auth/register', payload);

    if (response.success && response.data) {
      console.log('✅ [AuthService] Registration successful', {
        hasAccessToken: !!response.data.access_token,
        hasRefreshToken: !!response.data.refresh_token,
        expiresIn: response.data.expires_in
      });
      return response.data;
    } else {
      console.error('❌ [AuthService] Registration failed:', response.error);
      console.error('❌ [AuthService] Error details:', response.detail);

      // Better error message handling for 422 validation errors
      let errorMessage = response.error || 'Registration failed';
      if (response.detail) {
        // If detail is an array of validation errors, format them nicely
        if (Array.isArray(response.detail)) {
          const validationErrors = response.detail.map((err: any) => {
            if (typeof err === 'object' && err.msg && err.loc) {
              return `${err.loc.join('.')}: ${err.msg}`;
            }
            return String(err);
          }).join(', ');
          errorMessage = `Validation errors: ${validationErrors}`;
        } else if (typeof response.detail === 'string') {
          errorMessage = response.detail;
        }
      }

      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('🚨 [AuthService] Error during registration:', error);
    throw error instanceof Error ? error : new Error('Unknown registration error');
  }
};
